import React from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { defaultNS } from 'next-i18next.config';
import generateHreflang from 'lib/utils/generateHreflang';
import PATHS from 'lib/navigation/paths';
import { mapNamedLocaleToLocale } from 'lib/data/localesMap';
import dynamic from 'next/dynamic';
import { SLUGS_TYPES } from 'lib/utils/CONSTANTS';
import MobileDetect from 'mobile-detect';
import useDetectSlugsCSR from 'views/common/hooks/useDetectSlugsCSR';
import { handleServerResponse } from 'views/motorcycle-events-module/utils/handleServerResponse';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import { HydrationWrapper } from 'views/common/components';
import { useSelector } from 'react-redux';
import { checkIfUrlHasCorrectSegmentsTranslations } from 'lib/utils/checkIfUrlHasCorrectSegmentsTranslations';
import { ContentContext } from 'views/rebranded-module/common/context/useContent';
import { useEventsListingCMSData } from 'views/rebranded-module/events-module/MotorcycleEventsViewV2/hooks/useEventsListingCMSData';
import { useEventDetailsCMSData } from 'views/rebranded-module/events-module/MotorcycleEventsDetailsView/hooks/useEventDetailsCMSData';

const MotorcycleEventsViewV2 = dynamic(() => import('views/rebranded-module/events-module/MotorcycleEventsViewV2').then(
  (file) => file.MotorcycleEventsViewV2,
));
const MotorcycleEventsDetailsView = dynamic(() => import(
  'views/rebranded-module/events-module/MotorcycleEventsDetailsView'
).then((file) => file.MotorcycleEventsDetailsView));
const MotorcycleEventsPage = (props) => {
  const { serverData, slugTypeSSR, deviceType } = props;
  const listContent = useEventsListingCMSData();
  const appNavigator = useAppNavigator();
  const detailscontent = useEventDetailsCMSData();
  const isCrawler = useSelector((state) => state.globalData.isCrawler);
  const [slug, loadingSLug, handleSlugTypeChange] = useDetectSlugsCSR(
    slugTypeSSR,
    appNavigator.query.slug,
    {
      slugs: [
        {
          slug: decodeURIComponent(appNavigator.query.slug),
          expected_types: [
            SLUGS_TYPES.event,
            SLUGS_TYPES.event_type,
            SLUGS_TYPES.location,
          ],
        },
      ],
    },
    PATHS.motorcycleEvents.slug,
    [appNavigator.query.slug],
  );
  const getEventsParams = (slugType) => {
    let params = {};

    if (!serverData && appNavigator.query.slug) {
      if (slugType.slugs[0].slug_type === SLUGS_TYPES.location) {
        params = {
          location_id: slugType.slugs[0].resource_id,
          ...params,
        };
      } else {
        params = {
          event_type_id: slugType.slugs[0].resource_id,
          ...params,
        };
      }
    }
    return params;
  };

  const renderPage = () => {
    if (
      !appNavigator.query.slug
      || (slug?.slugs && slug.slugs[0].slug_type !== SLUGS_TYPES.event)
    ) {
      const params = getEventsParams(slug);
      return (
        <ContentContext.Provider value={listContent?.data}>
          <MotorcycleEventsViewV2
            serverData={serverData}
            slugParam={params}
            handleSlugTypeChange={handleSlugTypeChange}
            slugName={slug?.slugs[0].slug}
          />
        </ContentContext.Provider>
      );
    }
    if (slug?.slugs && slug.slugs[0].slug_type === SLUGS_TYPES.event) {
      return (
        <ContentContext.Provider value={detailscontent?.data}>
          <MotorcycleEventsDetailsView
            serverData={serverData}
            deviceType={deviceType}
            eventID={slug.slugs[0].resource_id}
          />
        </ContentContext.Provider>
      );
    }
    return <></>; // skeleton place
  };
  if (isCrawler) {
    return (
      <>{!loadingSLug && <HydrationWrapper>{renderPage()}</HydrationWrapper>}</>
    );
  }
  return <>{!loadingSLug && renderPage()}</>;
};
export async function getServerSideProps(ctx) {
  const { req, locale, query } = ctx;

  let serverData = null;
  let hreflangData;
  const slugSSR = query.slug || null;
  let deviceType;
  const userAgent = req.headers['user-agent'];
  serverData = await handleServerResponse(ctx);

  if (serverData?.newPath) {
    return {
      redirect: {
        destination: serverData.newPath,
        permanent: true,
      },
    };
  }
  if (serverData?.serverError?.statusCode) {
    ctx.res.statusCode = serverData?.serverError.statusCode;
  }
  // generate canonical and hrefLang
  if (slugSSR) {
    hreflangData = generateHreflang(
      PATHS.motorcycleEvents.slug(slugSSR).url,
      locale,
    );
  } else {
    hreflangData = generateHreflang(PATHS.motorcycleEvents.index().url, locale);
  }
  const md = new MobileDetect(userAgent);
  if (md.tablet()) {
    deviceType = 'tablet';
  } else if (md.mobile()) {
    deviceType = 'mobile';
  } else {
    deviceType = 'desktop';
  }

  const isCorrectSegemntTranslation = checkIfUrlHasCorrectSegmentsTranslations(
    ctx,
    hreflangData.canonical,
  );
  if (isCorrectSegemntTranslation.shouldRedirect) {
    return {
      redirect: {
        destination: isCorrectSegemntTranslation.redirectURL,
        permanent: true,
      },
    };
  }
  const pageOptions = {
    rebrandingPage: true,
    options: {
      transparentNavBar: true,
    },
  };
  return {
    props: {
      ...(await serverSideTranslations(mapNamedLocaleToLocale[locale], [
        ...defaultNS,
        'events_listing',
        'common_event_ride_card',
        'fe_er_best_price_guarantee',
        'fe_er_my_review',
        'event_details',
        'common_share',
        'common_participating_locations',
        'common_gallery',
        'fe_er_reviews_card',
        'fe_er_motorcycle_tours',
        'common_new_main_widget',
        'fe_er_common_main_widget',
        'fe_er_auth_overlay',
        'fe_er_auth',
      ])),
      ...serverData,
      ...pageOptions,
      hreflangData,
      deviceType,
    },
  };
}
export default MotorcycleEventsPage;
